import type { ChangeEvent, Dispatch, SetStateAction } from 'react';
import Card from '@components/UI/Card';
import MultiSelect, { type Option } from '@components/UI/MultiSelect';
import FormInput from '@components/UI/FormInput';
import FormNumberInput from '@components/UI/FormNumberInput';
import FormCheckbox from '@components/UI/FormCheckbox';
import FormSelect from '@components/UI/FormSelect';
import type { EntityType, TradingProfile } from '@einhaus/project314-types';

interface BasicSettingsCardProps {
    formData: TradingProfile;
    setFormData: Dispatch<SetStateAction<TradingProfile>>;
    handleInputChange: (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
    selectedEntityTypes: EntityType[];
    setSelectedEntityTypes: Dispatch<SetStateAction<EntityType[]>>;
}

const BasicSettingsCard = ({
    formData,
    setFormData,
    handleInputChange,
    selectedEntityTypes,
    setSelectedEntityTypes
}: BasicSettingsCardProps) => {
    return (
        <Card className="mb-4" title="Settings">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
                <div className="md:col-span-4 flex items-center">
                    <label className="text-zinc-200" htmlFor="settingsName">
                        Trading Profile Name
                    </label>
                </div>
                <div className="md:col-span-8">
                    <FormInput data-name="name" id="settingsName" type="text" value={formData.name} onChange={handleInputChange} />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
                <div className="md:col-span-4 flex items-center">
                    <label className="text-zinc-200" htmlFor="minOptionPrice">
                        Minimum Option Price
                    </label>
                </div>
                <div className="md:col-span-8">
                    <FormNumberInput
                        id="minOptionPrice"
                        min={0}
                        step={0.01}
                        value={formData.minOptionPrice}
                        onValueChange={(value) => setFormData((prev) => ({ ...prev, minOptionPrice: value }))}
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
                <div className="md:col-span-4 flex items-center">
                    <label className="text-zinc-200" htmlFor="backtestOptionTradeSizeDollars">
                        Historical Option Trade Size
                    </label>
                </div>
                <div className="md:col-span-8">
                    <FormNumberInput
                        id="backtestOptionTradeSizeDollars"
                        min={0.01}
                        max={1_000_000}
                        step={100}
                        value={formData.backtestOptionTradeSizeDollars ?? null}
                        onValueChange={(value) =>
                            setFormData((previous) => ({
                                ...previous,
                                backtestOptionTradeSizeDollars: value
                            }))
                        }
                    />
                    <p className="mt-2 text-sm text-zinc-400">
                        Optional budget for one standalone historical-backtest trade. Leave blank to use the $10,000 worker default. Live
                        and portfolio position sizing are unaffected.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
                <div className="md:col-span-4 flex items-center">
                    <label className="text-zinc-200" htmlFor="allowedUnderlyingBias">
                        Allowed Underlying Bias
                    </label>
                </div>
                <div className="md:col-span-8">
                    <FormSelect
                        id="allowedUnderlyingBias"
                        options={[
                            { value: 'both', label: 'Both' },
                            { value: 'bearish', label: 'Bearish' },
                            { value: 'bullish', label: 'Bullish' }
                        ]}
                        value={formData.allowedUnderlyingBias}
                        onChange={handleInputChange}
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
                <div className="md:col-span-4 flex items-center">
                    <label className="text-zinc-200" htmlFor="backtestMetricScope">
                        Backtest Metric Scope
                    </label>
                </div>
                <div className="md:col-span-8">
                    <FormSelect
                        id="backtestMetricScope"
                        options={[
                            { value: 'entity', label: 'Entity Only' },
                            { value: 'entityList', label: 'Strategy Entity List Cohort' },
                            { value: 'allEntities', label: 'All Entities (Global Same Backtest Recipe)' },
                            { value: 'blended', label: 'Blended Entity + Cohort' }
                        ]}
                        value={formData.backtestMetricScope}
                        onChange={handleInputChange}
                    />
                    <p className="mt-2 text-sm text-zinc-400">
                        Controls whether backtest filters and sorting use only this entity&apos;s history, the deployed strategy&apos;s
                        entity list, the full entity universe, or a reliability-weighted blend of entity history with the best available
                        cohort.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
                <div className="md:col-span-4 flex items-center">
                    <label className="text-zinc-200" htmlFor="backtestSelectionScope">
                        Best Backtest Selection Scope
                    </label>
                </div>
                <div className="md:col-span-8">
                    <FormSelect
                        id="backtestSelectionScope"
                        options={[
                            { value: 'entity', label: 'Entity Only' },
                            { value: 'entityList', label: 'Strategy Entity List' },
                            { value: 'allEntities', label: 'All Entities' }
                        ]}
                        value={formData.backtestSelectionScope ?? 'entity'}
                        onChange={(event) => {
                            const selectionScope = event.target.value as NonNullable<TradingProfile['backtestSelectionScope']>;
                            setFormData((previous) => ({
                                ...previous,
                                backtestSelectionScope: selectionScope,
                                ...(selectionScope !== 'entity' && (previous.backtestSelectionScope ?? 'entity') === 'entity'
                                    ? {
                                          backtestSelectionPolicyKey: 'cohort_robust_v2',
                                          backtestSelectionPolicyVersion: 2,
                                          backtestSelectionExecutionMode: 'shadow'
                                      }
                                    : selectionScope === 'entity'
                                      ? {
                                            backtestSelectionPolicyKey: 'profile_metric_v1',
                                            backtestSelectionPolicyVersion: 1,
                                            backtestSelectionExecutionMode: 'authoritative'
                                        }
                                      : {})
                            }));
                        }}
                    />
                    <p className="mt-2 text-sm text-zinc-400">
                        Chooses whether the winning exit/option recipe is ranked from this entity alone or from a frozen strategy cohort.
                        Cohort selection fails closed when its point-in-time snapshot is unavailable.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
                <div className="md:col-span-4 flex items-center">
                    <label className="text-zinc-200" htmlFor="backtestSelectionPolicyKey">
                        Best Backtest Selection Policy
                    </label>
                </div>
                <div className="md:col-span-8">
                    <FormSelect
                        disabled={(formData.backtestSelectionScope ?? 'entity') === 'entity'}
                        id="backtestSelectionPolicyKey"
                        options={[
                            { value: 'profile_metric_v1', label: 'Profile Metric V1' },
                            { value: 'cohort_robust_v1', label: 'Cohort Robust V1 (Compatibility)' },
                            { value: 'cohort_robust_v2', label: 'Cohort Robust V2' }
                        ]}
                        value={formData.backtestSelectionPolicyKey ?? 'profile_metric_v1'}
                        onChange={(event) => {
                            const policyKey = event.target.value as NonNullable<TradingProfile['backtestSelectionPolicyKey']>;
                            setFormData((previous) => ({
                                ...previous,
                                backtestSelectionPolicyKey: policyKey,
                                backtestSelectionPolicyVersion: policyKey === 'cohort_robust_v2' ? 2 : 1
                            }));
                        }}
                    />
                    <p className="mt-2 text-sm text-zinc-400">
                        Robust V2 emphasizes median performance, duration, breadth, downside risk, and concentration instead of letting a
                        few outlier trades dominate.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
                <div className="md:col-span-4 flex items-center">
                    <label className="text-zinc-200" htmlFor="backtestSelectionExecutionMode">
                        Selection Execution Mode
                    </label>
                </div>
                <div className="md:col-span-8">
                    <FormSelect
                        disabled={(formData.backtestSelectionScope ?? 'entity') === 'entity'}
                        id="backtestSelectionExecutionMode"
                        options={[
                            { value: 'shadow', label: 'Shadow (Observe Only)' },
                            { value: 'authoritative', label: 'Authoritative' }
                        ]}
                        value={
                            (formData.backtestSelectionScope ?? 'entity') === 'entity'
                                ? 'authoritative'
                                : (formData.backtestSelectionExecutionMode ?? 'shadow')
                        }
                        onChange={handleInputChange}
                    />
                    <p className="mt-2 text-sm text-zinc-400">
                        Shadow keeps entity-local selection authoritative while recording cohort decisions. Authoritative uses the cohort
                        result for entries. AERO remains the final exit authority when an AERO binding applies.
                    </p>
                </div>
            </div>

            {/* Allowed Entity Types Select */}
            <div className="flex flex-col md:flex-row md:justify-between gap-4 mb-4">
                <div className="flex-1">
                    <label className="block text-sm font-medium text-zinc-200" htmlFor="allowedEntityTypesSelect">
                        Allowed Entity Types
                    </label>
                </div>
                <div className="flex-1">
                    <MultiSelect
                        isMulti
                        className="w-full"
                        id="allowedEntityTypesSelect"
                        options={[
                            { value: 'Asset', label: 'Asset' },
                            { value: 'Cryptocurrency', label: 'Cryptocurrency' },
                            { value: 'AssetIndex', label: 'Asset Index' },
                            { value: 'FuturesProduct', label: 'Futures (research only)' }
                        ]}
                        placeholder="Select entity types..."
                        styles={{
                            control: (base) => ({
                                ...base,
                                backgroundColor: '#1e1e1e',
                                borderColor: '#444',
                                color: '#fff'
                            }),
                            menu: (base) => ({
                                ...base,
                                backgroundColor: '#2c2c2c',
                                color: '#fff',
                                borderColor: '#444'
                            }),
                            option: (base, state) => ({
                                ...base,
                                backgroundColor: state.isFocused ? '#3d3d3d' : '#2c2c2c',
                                color: '#fff'
                            }),
                            multiValue: (base) => ({
                                ...base,
                                backgroundColor: '#3d3d3d'
                            }),
                            multiValueLabel: (base) => ({
                                ...base,
                                color: '#fff'
                            }),
                            multiValueRemove: (base) => ({
                                ...base,
                                color: '#fff',
                                ':hover': {
                                    backgroundColor: '#555',
                                    color: '#fff'
                                }
                            }),
                            input: (base) => ({
                                ...base,
                                color: '#fff'
                            }),
                            singleValue: (base) => ({
                                ...base,
                                color: '#fff'
                            }),
                            placeholder: (base) => ({
                                ...base,
                                color: '#aaa'
                            }),
                            indicatorSeparator: (base) => ({
                                ...base,
                                backgroundColor: '#444'
                            }),
                            dropdownIndicator: (base) => ({
                                ...base,
                                color: '#aaa'
                            }),
                            clearIndicator: (base) => ({
                                ...base,
                                color: '#aaa'
                            })
                        }}
                        value={selectedEntityTypes.map((type) => ({
                            value: type,
                            label: type === 'AssetIndex' ? 'Asset Index' : type
                        }))}
                        onChange={(selectedOptions) => {
                            if (!selectedOptions) {
                                setSelectedEntityTypes([]);

                                setFormData((prev) => ({
                                    ...prev,
                                    allowedUnderlyingEntityTypes: []
                                }));

                                return;
                            }

                            const selected = (selectedOptions as Option[]).map((option) => option.value.toString() as EntityType);

                            setSelectedEntityTypes(selected);

                            // Update the form data with the selected entity types
                            setFormData((prev) => ({
                                ...prev,
                                allowedUnderlyingEntityTypes: selected
                            }));
                        }}
                    />
                </div>
            </div>

            {/* Checkboxes for FOMC and earnings */}
            <div className="flex items-center mb-3">
                <FormCheckbox
                    checked={formData.deactivateOnFomcDays}
                    id="deactivateOnFomcDays"
                    label="Deactivate On FOMC Days"
                    onChange={handleInputChange}
                />
            </div>

            <div className="flex items-center mb-3">
                <FormCheckbox
                    checked={formData.autoLiquidateAssignedShares}
                    id="autoLiquidateAssignedShares"
                    label="Auto-liquidate Option Settlement Shares"
                    onChange={handleInputChange}
                />
            </div>

            {formData.autoLiquidateAssignedShares && (
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
                    <div className="md:col-span-4 flex items-center">
                        <label className="text-zinc-200" htmlFor="assignedShareLiquidationMinutesAfterOpen">
                            Minutes After Open
                        </label>
                    </div>
                    <div className="md:col-span-8">
                        <FormNumberInput
                            integer
                            id="assignedShareLiquidationMinutesAfterOpen"
                            max={390}
                            min={0}
                            value={formData.assignedShareLiquidationMinutesAfterOpen}
                            onValueChange={(value) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    assignedShareLiquidationMinutesAfterOpen: value
                                }))
                            }
                        />
                    </div>
                </div>
            )}
        </Card>
    );
};

export default BasicSettingsCard;
